import BN from "bn.js";
import Decimal from "decimal.js";
import { TIME } from "@bridgesplit/utils";
import { WhirlpoolOffchain, OracleQuote } from "@bridgesplit/abf-sdk";

export type YieldData = {
    positionYieldOverTvl24h: number;
    positionYieldOverTvlAnnual: number;
    positionUsdValue: number;
};

export enum YieldPeriodSelection {
    DAILY = "daily",
    ANNUAL = "annual"
}

export function getPositionYield({
    positionLiquidity,
    poolLiquidity,
    whirlpoolOffchain,
    positionUsdValue,
    tokenAAmount,
    tokenBAmount,
    tokenAMint,
    tokenBMint,
    collateralYields,
    oraclePrices
}: {
    positionUsdValue: number;
    positionLiquidity: BN;
    poolLiquidity: BN;
    whirlpoolOffchain: WhirlpoolOffchain;
    tokenAAmount: number;
    tokenBAmount: number;
    tokenAMint: string;
    tokenBMint: string;
    collateralYields: Record<string, number> | undefined;
    oraclePrices: Map<string, OracleQuote | undefined> | undefined;
}): YieldData {
    let positionYieldOverTvlAnnual = 0;

    // Check if we have the required data for yield calculation
    if (positionUsdValue === 0 || positionLiquidity.isZero() || poolLiquidity.isZero()) {
        return { positionYieldOverTvl24h: 0, positionYieldOverTvlAnnual: 0, positionUsdValue };
    }

    // Calculate base whirlpool yield if we have both token amounts
    if (tokenAAmount > 0 && tokenBAmount > 0) {
        const liquidityShare = new Decimal(positionLiquidity.toString()).div(poolLiquidity.toString()).toNumber();
        const positionYield = liquidityShare * parseFloat(whirlpoolOffchain.stats["24h"].fees);
        const positionYieldOverTvl24h = positionYield / positionUsdValue;
        positionYieldOverTvlAnnual = positionYieldOverTvl24h * (TIME.YEAR / TIME.DAY);
    }

    // Check if token_a and token_b have additional yield from collateral yield history
    let additionalYield = 0;

    // Get token_a yield if available
    if (collateralYields && oraclePrices) {
        const tokenAYield = collateralYields[tokenAMint];
        if (tokenAYield !== undefined) {
            const tokenAPrice = oraclePrices.get(tokenAMint);

            if (tokenAPrice) {
                const tokenAValue = tokenAAmount * tokenAPrice.usdPrice;
                const tokenAWeight = tokenAValue / positionUsdValue;
                additionalYield += tokenAYield * tokenAWeight;
            }
        }

        // Get token_b yield if available
        const tokenBYield = collateralYields[tokenBMint];
        if (tokenBYield !== undefined) {
            const tokenBPrice = oraclePrices.get(tokenBMint);

            if (tokenBPrice) {
                const tokenBValue = tokenBAmount * tokenBPrice.usdPrice;
                const tokenBWeight = tokenBValue / positionUsdValue;
                additionalYield += tokenBYield * tokenBWeight;
            }
        }
    }

    const finalYieldOverTvlAnnual = positionYieldOverTvlAnnual + additionalYield;
    const finalYieldOverTvl24h = finalYieldOverTvlAnnual / (TIME.YEAR / TIME.DAY);

    return {
        positionYieldOverTvl24h: finalYieldOverTvl24h,
        positionYieldOverTvlAnnual: finalYieldOverTvlAnnual,
        positionUsdValue
    };
}
